import {
    ProductInfo,
    SelectedPacket,
    ProductWithAddOns,
    AddOnInfo,
    ProductPriceType,
    PackageDiscount
} from 'components/enrollForm/enrollFormTypes'
import { constants, isDateExpired } from 'helpers'
import { ClientType, Order } from 'storeTypes'

type CatalogDiscountSource = {
    discount?: PackageDiscount | null
    discounts?: PackageDiscount | null
}

const hasDiscountTiers = (
    value?: PackageDiscount | null
): value is PackageDiscount => Boolean(value?.tiers?.length)

export const getCatalogPackageDiscount = (
    source?: CatalogDiscountSource | null,
    fallback?: CatalogDiscountSource | null
): PackageDiscount | undefined => {
    const candidates = [
        source?.discounts,
        source?.discount,
        fallback?.discounts,
        fallback?.discount
    ]

    return candidates.find(hasDiscountTiers)
}

export const convertProductPricesToAddOns = (
    product: ProductInfo,
    addOnsForProduct?: ProductPriceType[]
): AddOnInfo[] => {
    addOnsForProduct ??= product.product_prices
    return addOnsForProduct.map((productPrice) => {
        const discount = getCatalogPackageDiscount(productPrice, product)
        return {
            package_id: productPrice.sf_id,
            product_id: product.sf_id,
            product_name: product.name,
            pricebook_id: productPrice.pricebook.sf_id,
            price: productPrice.price,
            size: productPrice.package_size,
            size_display: productPrice.package_size_display,
            unit: productPrice.units_of_measure,
            hide_price: product.hide_price,
            depends_on: productPrice.depends_on,
            image_url: product.image_url,
            ...(discount ? { discount } : {})
        }
    })
}

export const findAddOnForProduct = (
    productList: ProductInfo[],
    packageIds: string[]
): AddOnInfo[] => {
    if (!productList || !packageIds || packageIds.length === 0) return []

    return productList.reduce((acc: AddOnInfo[], product) => {
        const addOnsForProduct =
            product.product_prices?.filter((productPrice) => {
                const dependsOnList = productPrice.depends_on || []
                return dependsOnList.some((id) => packageIds.includes(id))
            }) || []

        const addOnInfos: AddOnInfo[] = convertProductPricesToAddOns(
            product,
            addOnsForProduct
        )

        return acc.concat(addOnInfos)
    }, [])
}

export const findAddOnsForSelectedPackages = (
    product: ProductWithAddOns,
    selectedPackets: SelectedPacket[]
) => {
    const packageIds = selectedPackets
        ?.map((packet) => {
            if (packet.product_id === product.sf_id) {
                return packet.package_id
            } else {
                return undefined
            }
        })
        .filter((packageId) => packageId !== undefined)
    return product.addOns.filter((addOn) =>
        addOn.depends_on.some((dependsOn) => packageIds.includes(dependsOn))
    )
}

export const isProductExpired = (
    product: ProductInfo,
    currentClient: ClientType
) => {
    let goalItems = [] as Order[]
    if (
        currentClient?.form_type === 'modification' ||
        currentClient?.modification ===
            constants.CLIENT_MODIFICATION_TYPES.PACKAGES
    ) {
        goalItems = currentClient?.goal_items || []
    }
    return (
        product.end_date &&
        isDateExpired(product.end_date) &&
        !goalItems.some(
            (goalItem: Order) => goalItem.product.id === product.sf_id
        )
    )
}

const isPackageHidden = (
    productPackage: ProductPriceType,
    currentClient: ClientType
) => {
    if (
        (currentClient?.form_type === 'modification' ||
            currentClient?.modification ===
                constants.CLIENT_MODIFICATION_TYPES.PACKAGES) &&
        currentClient.goal_items?.find(
            (goalItem: Order) => goalItem.package_id === productPackage.sf_id
        ) !== undefined
    ) {
        return false
    }
    return productPackage.hide_from_ve === 'True'
}

export const buildProductListWithAddOns = (
    productList: ProductInfo[],
    currentClient: ClientType
) => {
    if (!productList?.length) return []
    const activeProducts = productList.filter(
        (product) => !isProductExpired(product, currentClient)
    )
    return activeProducts.map((product: ProductInfo): ProductWithAddOns => {
        const productPrices = (product.product_prices || []).filter(
            (productPrice: ProductPriceType) =>
                !isPackageHidden(productPrice, currentClient)
        )
        const addOns = findAddOnForProduct(
            productList,
            (product.product_prices || []).flatMap(
                (productPrice) => productPrice.sf_id
            )
        )
        return {
            ...product,
            product_prices: productPrices,
            nbSelectedUnits: 0,
            addOns,
            isAddOn:
                productPrices.filter(
                    (productPrice) => !!productPrice.depends_on?.length
                ).length > 0
        }
    })
}

export const updateNbSelectedUnits = (
    productList: ProductWithAddOns[],
    selectedPackets: SelectedPacket[]
) => {
    return productList.map((product) => {
        let productCount = 0
        selectedPackets.forEach((packet) => {
            if (product.sf_id === packet.product_id) {
                productCount += 1
            }
        })
        return {
            ...product,
            nbSelectedUnits: productCount
        }
    })
}

export const findParentProducts = (
    packageId: string,
    productListWithAddons: ProductWithAddOns[]
) => {
    return productListWithAddons.filter((product) =>
        product.addOns.some((addOn) => addOn.package_id === packageId)
    )
}

export const dropAddOnsWithoutParentSelected = (
    selectedPackets: SelectedPacket[],
    productListWithAddOns: ProductWithAddOns[]
) => {
    return selectedPackets.filter((packet) => {
        if (packet.isAddOn) {
            const addOnParentProducts = findParentProducts(
                packet.package_id,
                productListWithAddOns
            )
            const addOnHasParentSelected = addOnParentProducts.some(
                (product) => product.nbSelectedUnits > 0
            )
            if (!addOnHasParentSelected) {
                return false
            }
        }
        return true
    })
}

export const resetProductPosition = (
    sortedList: ProductWithAddOns[],
    originalProductList: ProductInfo[],
    selectedProduct: ProductWithAddOns
) => {
    if (!sortedList?.length || !selectedProduct) {
        return sortedList
    }
    const selectedProductOriginalIndex = originalProductList.findIndex(
        (p) => p.sf_id === selectedProduct.sf_id
    )
    const productsAfterInOriginal = originalProductList.slice(
        selectedProductOriginalIndex + 1
    )
    const productIndex = sortedList.findIndex(
        (p) => p.sf_id === selectedProduct.sf_id
    )
    let indexToInsert = sortedList.length - 1
    const [productToMove] = sortedList.splice(productIndex, 1)
    for (const productAfterInOriginal of productsAfterInOriginal) {
        const markerIndexInOrderedList = sortedList.findIndex(
            (p) => p.sf_id === productAfterInOriginal.sf_id
        )
        if (
            markerIndexInOrderedList !== -1 &&
            sortedList[markerIndexInOrderedList].nbSelectedUnits === 0
        ) {
            indexToInsert = markerIndexInOrderedList
            break
        }
    }
    sortedList.splice(indexToInsert, 0, productToMove)
    return sortedList
}
/*
    Place selected products at the top of the list
    Place the selected addons after the selected products
    If product is dropped, place it back at its original position
*/
export const sortProductListWithAddOns = (
    updatedProductList: ProductWithAddOns[],
    originalProductList: ProductInfo[],
    selectedProduct: ProductWithAddOns
): ProductWithAddOns[] => {
    if (!updatedProductList?.length || !selectedProduct) {
        return updatedProductList
    }
    const hasUnitSelected = selectedProduct.nbSelectedUnits > 0

    const sortedList = [...updatedProductList]
    const productIndex = sortedList.findIndex(
        (p) => p.sf_id === selectedProduct.sf_id
    )
    if (productIndex === -1) {
        return sortedList
    }

    if (hasUnitSelected) {
        let insertIndex = 0
        for (let i = 0; i < sortedList.length; i++) {
            if (
                sortedList[i].nbSelectedUnits === 0 ||
                (sortedList[i].isAddOn && !selectedProduct.isAddOn)
            ) {
                insertIndex = i
                break
            }
        }
        // Reorder only if product hasn't been reordered yet
        if (insertIndex < productIndex) {
            const [productToMove] = sortedList.splice(productIndex, 1)
            sortedList.splice(insertIndex, 0, productToMove)
        }
    } else {
        return resetProductPosition(
            sortedList,
            originalProductList,
            selectedProduct
        )
    }

    return sortedList
}
